import { useTranslation } from "react-i18next";
import Chip, { type ChipProps, type ChipSize } from "../../components/Chip";
import { Text } from "../../components";
import {
  OrderItemStatus,
  OrderItemUnifiedStatus,
  OrderStatus,
  PaymentStatus,
  TpaApprovalStatus,
} from "../../apis/order";

export function getOrderStatusChipProps(
  status: string,
  t: (key: string, options?: Record<string, unknown>) => string,
): Pick<ChipProps, "label" | "type" | "solid"> {
  const normalized = status.trim().toUpperCase();

  switch (normalized) {
    case OrderStatus.draft:
      return { label: t("general.draft"), type: "neutral" };
    case OrderStatus.inProgress:
    case OrderItemStatus.INPROGRESS:
    case OrderItemUnifiedStatus.CHECKED_IN:
      return { label: t("general.checkedIn"), type: "info" };
    case OrderStatus.confirmed:
    case OrderItemUnifiedStatus.CONFIRMED:
      return { label: t("general.confirmed"), type: "success" };
    case OrderStatus.complete:
    case OrderItemStatus.COMPLETE:
    case OrderItemUnifiedStatus.COMPLETE:
      return { label: t("general.completed"), type: "success", solid: true };
    case OrderStatus.cancelled:
    case OrderItemStatus.CANCELLED:
    case OrderItemUnifiedStatus.CANCELLED:
      return { label: t("general.cancelled"), type: "warning" };
    case OrderStatus.noShow:
    case OrderItemStatus.NO_SHOW:
    case OrderItemUnifiedStatus.NO_SHOW:
      return { label: t("general.noShow"), type: "caution" };
    case OrderItemStatus.PENDING:
    case OrderItemUnifiedStatus.PENDING:
    case PaymentStatus.pendingTpaApproval:
      return { label: t("general.pending"), type: "warning" };
    case OrderItemStatus.RESCHEDULED:
      return { label: t("general.rescheduled"), type: "info" };
    case OrderItemUnifiedStatus.APPROVED:
      return { label: t("general.approved"), type: "verified" };
    case OrderItemUnifiedStatus.REJECTED:
      return { label: t("general.rejected"), type: "critical" };
    case PaymentStatus.paymentPending:
      return { label: t("general.paymentPending"), type: "caution" };
    case PaymentStatus.paid:
      return { label: t("general.paid"), type: "success" };
    case PaymentStatus.failed:
      return { label: t("general.failed"), type: "critical" };
    case TpaApprovalStatus.approvedForBilling:
      return {
        label: t("manageBookings.approval.approvedAlert"),
        type: "success",
      };
    case TpaApprovalStatus.rejectedByTpa:
      return {
        label: t("manageBookings.approval.rejectedAlert"),
        type: "critical",
      };
    case TpaApprovalStatus.needsReview:
      return { label: t("general.needsReview"), type: "warning" };
    default:
      return { label: status, type: "neutral" };
  }
}

export function OrderStatusChip({
  status,
  size = "sm",
}: {
  status?: string | null;
  size?: ChipSize;
}) {
  const { t } = useTranslation();
  if (!status?.trim()) return <Text>{"--"}</Text>;

  return <Chip size={size} {...getOrderStatusChipProps(status, t)} />;
}
